// src/hooks/useRevenueAnalytics.js
import { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const useRevenueAnalytics = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('sentAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setInvoices(
          snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              sentAt: data.sentAt?.toDate() || null,
              dueDate: data.dueDate?.toDate() || null,
            };
          })
        );
        setLoading(false);
      },
      (err) => {
        console.error('Revenue analytics error:', err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    const paid = invoices.filter((i) => i.stripeInvoiceStatus === 'paid');
    const open = invoices.filter(
      (i) => !i.stripeInvoiceStatus || i.stripeInvoiceStatus === 'open'
    );
    const failed = invoices.filter((i) => i.stripeInvoiceStatus === 'payment_failed');

    const thisMonthPaid = paid.filter((i) => i.sentAt && i.sentAt >= startOfMonth);
    const lastMonthPaid = paid.filter(
      (i) => i.sentAt && i.sentAt >= startOfLastMonth && i.sentAt <= endOfLastMonth
    );

    const totalRevenue = paid.reduce((s, i) => s + (i.totalAmount || 0), 0);
    const monthlyRevenue = thisMonthPaid.reduce((s, i) => s + (i.totalAmount || 0), 0);
    const lastMonthRevenue = lastMonthPaid.reduce((s, i) => s + (i.totalAmount || 0), 0);
    const totalOutstanding = open.reduce((s, i) => s + (i.totalAmount || 0), 0);
    const avgInvoice = paid.length > 0 ? totalRevenue / paid.length : 0;
    const paymentRate =
      invoices.length > 0 ? Math.round((paid.length / invoices.length) * 100) : 0;

    const monthlyChange =
      lastMonthRevenue > 0
        ? Math.round(((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100)
        : monthlyRevenue > 0
        ? 100
        : 0;

    // Last 6 months trend
    const monthlyTrend = [];
    for (let i = 5; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const label = start.toLocaleString('default', { month: 'short' });
      const revenue = paid
        .filter((inv) => inv.sentAt && inv.sentAt >= start && inv.sentAt <= end)
        .reduce((s, inv) => s + (inv.totalAmount || 0), 0);
      const invoiceCount = invoices.filter(
        (inv) => inv.sentAt && inv.sentAt >= start && inv.sentAt <= end
      ).length;
      monthlyTrend.push({ month: label, revenue, invoices: invoiceCount });
    }

    return {
      totalRevenue,
      monthlyRevenue,
      lastMonthRevenue,
      monthlyChange,
      totalOutstanding,
      avgInvoice,
      paymentRate,
      totalInvoices: invoices.length,
      paidCount: paid.length,
      openCount: open.length,
      failedCount: failed.length,
      monthlyTrend,
    };
  }, [invoices]);

  return { invoices, stats, loading, error };
};

export default useRevenueAnalytics;